import {
  addGameObject,
  anchoredPos,
  getAbsolutePosAndScale,
  getZeroStaticEntity,
  setParent,
} from '../entity';
import { colorToHex, rotateV2, v2 } from '../helpers';
import { isMouseDown, mouseDownThisFrame, setVisibilityState } from '../simulate';
import {
  CANVAS_TEXT_CAP_MARGIN_RATIO,
  DEFAULT_TEXT_STYLE,
  RICH_TEXT_STYLES,
  SHAPE_STYLES,
  TEXTOBJ2_HANDLE_SIZE,
  TEXTOBJ2_MIN_HEIGHT,
  TEXTOBJ2_MIN_WIDTH,
} from '../constants';
import * as Container from '../container';
import { COLORS } from '../constants-colors';

import {
  ServerEvent_EntityProps,
  ObjectType,
  type ServerEvent_TextObject2,
} from '../shared/api_shared_types';
import {
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  GameObject,
  Shape,
  V2,
  type TextObject2,
  RenderItemType,
  TextAlignHor,
} from '../types';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
  type RenderPackageText,
} from '../render_package';
import * as TextLayoutController from '../controllers/text_layout_controller';
import { setMouseCursor, resetMouseCursor } from '../window_layout';
import { TextObject2EditMode, type Line, type Segment } from '../textobject/textobject2_types';
import type { TextStyle } from '../types';
import logger from '../controllers/logger';
export { TextObject2EditMode } from '../textobject/textobject2_types';

export function controller(): GameObjectController {
  return {
    getNew: getZeroTextObject2,
    sync,
    update,
    createPackage,
    dispose,
  };
}

function dispose(obj: GameObject) {
  const tObj = obj as TextObject2;
  if (tObj.editTextArea !== null) {
    tObj.editTextArea.remove();
    tObj.editTextArea = null;
  }
  tObj._isEditing = false;
  tObj._pendingRemoval = false;
  tObj._dragSelectionAnchor = null;
  if (tObj._hasTextCursor) {
    tObj._hasTextCursor = false;
    resetMouseCursor();
  }
}

export function getZeroTextObject2(ent: Entity) {
  let newTextObject: TextObject2 = {
    ...ent,
    objectType: ObjectType.TEXTOBJECT2,
    textOverflow: 'ellipsis',
    editMode: TextObject2EditMode.STATIC,
    isRichText: false,

    defaultTextStyle: DEFAULT_TEXT_STYLE,

    lines: [],

    text: '',
    maxLength: 0,
    canEditSize: false,
    handle: null,
    padding: [40, 40, 40, 40],
    editTextArea: null,

    _wrapWidth: 0,
    _editAreaDirty: false,
    _isEditing: false,
    _pendingRemoval: false,
    _pendingFocus: false,
    _pendingCaretOffset: 0,
    _dragSelectionAnchor: null,
    _hasTextCursor: false,
  };

  return newTextObject;
}

function sync(manager: GameManager, textObject: TextObject2, data: ServerEvent_TextObject2) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if (data.hasOwnProperty('width')) {
    textObject.width = data.width;
  }

  if (data.hasOwnProperty('height')) {
    textObject.height = data.height;
  }

  if (data.hasOwnProperty('wrapText')) {
    textObject.textOverflow = data.wrapText ? 'wrap' : '';
  }

  let richTextChanged = false;
  if ('isRichText' in data) {
    const isRichText = data.isRichText === true;
    richTextChanged = textObject.isRichText !== isRichText;
    textObject.isRichText = isRichText;
    if (textObject.isRichText) {
      textObject.editMode = TextObject2EditMode.STATIC;
    }
  }

  if (!textObject.id) {
    addGameObject(manager, textObject);
    initTextObject(manager, textObject);

    // setParentByEntityProps(manager, textObject, data.sId, data.roomId, entityProps);
  }

  if ('text' in data && textObject.text !== data.text) {
    setText(textObject, data.text);
  } else if (richTextChanged) {
    // The mode flipped but the text value is unchanged — re-lay-out with the current text.
    setText(textObject, textObject.text, true);
  }
}

export function initTextObject(manager: GameManager, textObject: TextObject2): TextObject2 {
  if (textObject.shape === Shape.NONE) {
    textObject.shape = Shape.RECT;
  }

  textObject.allowHighlight = true;
  textObject.clickable = true;
  textObject.allowDrag = false;

  setVisibilityState(textObject, VisibilityState.DEFAULT);

  const handleEnt = getZeroStaticEntity();
  handleEnt.width = TEXTOBJ2_HANDLE_SIZE;
  handleEnt.height = TEXTOBJ2_HANDLE_SIZE;
  handleEnt.anchoredPos = anchoredPos(v2(0.5, 0.5), v2(0, 0), v2(0.5, 0.5));
  const handle = Container.createAndAddLocalContainer(manager, handleEnt);
  setParent(handle, textObject);
  textObject.handle = handle;

  return textObject;
}

export function createAndAddLocalTextObject2(
  manager: GameManager,
  ent: Entity,
  props: Partial<TextObject2>,
): TextObject2 {
  const tObj = {
    ...getZeroTextObject2(ent),
    ...props,
  };

  addGameObject(manager, tObj);

  initTextObject(manager, tObj);

  if (props.text) {
    setText(tObj, props.text, true);
  }

  return tObj;
}

function breakLongWord(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  word: string,
): string {
  const innerWidth = tObj.width - tObj.padding[1] - tObj.padding[3];
  let remaining = word;

  for (let guard = 0; guard < word.length; guard++) {
    const remainingWidth = TextLayoutController.getTextWidth(
      ctx,
      tObj.defaultTextStyle.fontId,
      tObj.defaultTextStyle.fontSize,
      remaining,
    );
    if (remainingWidth <= innerWidth) {
      return remaining;
    }

    let lo = 0;
    let hi = remaining.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const w = TextLayoutController.getTextWidth(
        ctx,
        tObj.defaultTextStyle.fontId,
        tObj.defaultTextStyle.fontSize,
        remaining.slice(0, mid),
      );
      if (w <= innerWidth) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }

    const cutAt = lo > 0 ? lo : 1;
    pushLine(tObj, ctx, remaining.slice(0, cutAt), tObj.height);
    remaining = remaining.slice(cutAt);
  }

  return remaining;
}

function pushLine(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  lineText: string,
  yPosition: number,
): Line {
  const width = TextLayoutController.getTextWidth(
    ctx,
    tObj.defaultTextStyle.fontId,
    tObj.defaultTextStyle.fontSize,
    lineText,
  );
  const height = tObj.defaultTextStyle.fontSize * tObj.defaultTextStyle.lineHeight;
  const line = { text: lineText, visibleText: lineText, width, height, yPosition };
  tObj.lines.push(line);
  tObj.height += height;

  return line;
}

function tokenizeParagraph(paragraph: string): string[] {
  const tokens: string[] = [];
  for (const spacePart of paragraph.split(' ')) {
    const dashParts = spacePart.split('-');
    for (let d = 0; d < dashParts.length - 1; d++) {
      tokens.push(dashParts[d] + '-');
    }
    tokens.push(dashParts[dashParts.length - 1]);
  }
  return tokens;
}

function joinTokens(tokens: string[]): string {
  let result = tokens[0] || '';
  for (let i = 1; i < tokens.length; i++) {
    result += tokens[i - 1].endsWith('-') ? tokens[i] : ' ' + tokens[i];
  }
  return result;
}

type RichRun = { text: string; style: TextStyle };
type StyledToken = { text: string; style: TextStyle };
type RichParseState = {
  defaultStyle: TextStyle;
  stack: Partial<TextStyle>[];
  runs: RichRun[];
  currentStyle: TextStyle;
  buffer: string;
};

const OPEN_TAG_REGEX = /^<([\w-]+)>/;
const CLOSE_TAG_REGEX = /^<\/([\w-]+)>/;

// Merges the default style with each Partial<TextStyle> on the stack, in order.
// Returns a fresh, fully-populated TextStyle.
function resolveStyle(defaultStyle: TextStyle, stack: Partial<TextStyle>[]): TextStyle {
  const merged: TextStyle = { ...defaultStyle };
  for (let i = 0; i < stack.length; i++) {
    Object.assign(merged, stack[i]);
  }
  return merged;
}

function flushRichBuffer(state: RichParseState) {
  if (state.buffer.length > 0) {
    state.runs.push({ text: state.buffer, style: state.currentStyle });
    state.buffer = '';
  }
}

function applyCloseTag(state: RichParseState) {
  flushRichBuffer(state);
  if (state.stack.length > 0) {
    state.stack.pop();
  }
  state.currentStyle = resolveStyle(state.defaultStyle, state.stack);
}

function applyOpenTag(state: RichParseState, slug: string) {
  flushRichBuffer(state);
  const partial = RICH_TEXT_STYLES[slug];
  if (partial) {
    state.stack.push(partial);
  } else {
    logger.warn(`richText: unknown style slug "${slug}"`);
    state.stack.push({});
  }
  state.currentStyle = resolveStyle(state.defaultStyle, state.stack);
}

// Tries to consume a tag at the start of `rest`. Returns the number of chars consumed,
// or 0 if there is no tag there (so the caller treats '<' as a literal).
function consumeTag(state: RichParseState, rest: string): number {
  const closeMatch = rest.match(CLOSE_TAG_REGEX);
  if (closeMatch !== null) {
    applyCloseTag(state);
    return closeMatch[0].length;
  }
  const openMatch = rest.match(OPEN_TAG_REGEX);
  if (openMatch !== null) {
    applyOpenTag(state, openMatch[1]);
    return openMatch[0].length;
  }
  return 0;
}

// Parses rich markup (<slug>...</slug>, nesting allowed) into a flat list of styled runs.
// Newlines are preserved inside run text and drive line splitting downstream.
function parseRichText(text: string, defaultStyle: TextStyle): RichRun[] {
  const state: RichParseState = {
    defaultStyle,
    stack: [],
    runs: [],
    currentStyle: resolveStyle(defaultStyle, []),
    buffer: '',
  };

  let i = 0;
  while (i < text.length) {
    const consumed = text.charAt(i) === '<' ? consumeTag(state, text.slice(i)) : 0;
    if (consumed > 0) {
      i += consumed;
    } else {
      state.buffer += text.charAt(i);
      i += 1;
    }
  }

  flushRichBuffer(state);

  if (state.runs.length === 0) {
    return [{ text: '', style: { ...defaultStyle } }];
  }
  return state.runs;
}

function styledTokenWidth(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  style: TextStyle,
  text: string,
): number {
  return TextLayoutController.getTextWidth(ctx, style.fontId, style.fontSize, text);
}

function sameStyle(a: TextStyle, b: TextStyle): boolean {
  return (
    a.fontId === b.fontId &&
    a.fontSize === b.fontSize &&
    a.lineHeight === b.lineHeight &&
    a.color[0] === b.color[0] &&
    a.color[1] === b.color[1] &&
    a.color[2] === b.color[2]
  );
}

function growSegment(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  segment: Segment,
  extra: string,
) {
  segment.text += extra;
  segment.visibleText = segment.text;
  segment.width = styledTokenWidth(ctx, segment.style, segment.text);
}

// Appends a styled token to the segment list, merging into the previous segment when the
// resolved style matches, otherwise starting a new segment. Tokens already carry their own
// trailing whitespace, so segments are built by plain concatenation (no separator inserted).
function appendTokenToSegments(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  segments: Segment[],
  token: StyledToken,
) {
  const prev = segments.length > 0 ? segments[segments.length - 1] : null;

  if (prev !== null && sameStyle(prev.style, token.style)) {
    growSegment(ctx, prev, token.text);
    return;
  }

  segments.push({
    text: token.text,
    visibleText: token.text,
    width: styledTokenWidth(ctx, token.style, token.text),
    style: token.style,
  });
}

function measureSegments(
  tObj: TextObject2,
  segments: Segment[],
): { width: number; height: number; text: string } {
  let width = 0;
  let height = tObj.defaultTextStyle.fontSize * tObj.defaultTextStyle.lineHeight;
  let text = '';
  for (let i = 0; i < segments.length; i++) {
    width += segments[i].width;
    text += segments[i].text;
    const segHeight = segments[i].style.fontSize * segments[i].style.lineHeight;
    if (segHeight > height) {
      height = segHeight;
    }
  }
  return { width, height, text };
}

// Builds a Line from a list of styled tokens, merging adjacent same-style tokens into segments.
function pushLineRich(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  tokens: StyledToken[],
  yPosition: number,
): Line {
  const segments: Segment[] = [];
  for (let i = 0; i < tokens.length; i++) {
    appendTokenToSegments(ctx, segments, tokens[i]);
  }

  const metrics = measureSegments(tObj, segments);
  const line: Line = {
    text: metrics.text,
    visibleText: metrics.text,
    width: metrics.width,
    height: metrics.height,
    yPosition,
    segments,
  };
  tObj.lines.push(line);
  tObj.height += metrics.height;

  return line;
}

// Splits an over-wide styled token, pushing full lines for all but the trailing remainder,
// which is returned for the caller to keep packing.
function breakLongWordRich(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  token: StyledToken,
): StyledToken {
  const innerWidth = tObj.width - tObj.padding[1] - tObj.padding[3];
  let remaining = token.text;

  for (let guard = 0; guard < token.text.length; guard++) {
    if (styledTokenWidth(ctx, token.style, remaining) <= innerWidth) {
      return { text: remaining, style: token.style };
    }

    let lo = 0;
    let hi = remaining.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (styledTokenWidth(ctx, token.style, remaining.slice(0, mid)) <= innerWidth) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }

    const cutAt = lo > 0 ? lo : 1;
    pushLineRich(tObj, ctx, [{ text: remaining.slice(0, cutAt), style: token.style }], tObj.height);
    remaining = remaining.slice(cutAt);
  }

  return { text: remaining, style: token.style };
}

// Finds where the wrap token starting at `start` ends: after the word's trailing space(s),
// or right after a hyphen (a break opportunity), or at end of fragment.
function nextTokenEnd(fragment: string, start: number): number {
  let j = start;
  while (j < fragment.length && fragment.charAt(j) !== ' ' && fragment.charAt(j) !== '-') {
    j += 1;
  }
  if (j < fragment.length && fragment.charAt(j) === '-') {
    return j + 1;
  }
  while (j < fragment.length && fragment.charAt(j) === ' ') {
    j += 1;
  }
  return j;
}

// Splits a fragment into wrap tokens that preserve exact text: each token is a word with its
// trailing space(s), or a word ending at a hyphen. Concatenating the tokens reproduces the
// fragment verbatim, so no separator guessing is needed when joining segments.
function tokenizeRichFragment(fragment: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  while (i < fragment.length) {
    const end = nextTokenEnd(fragment, i);
    tokens.push(fragment.slice(i, end));
    i = end;
  }
  return tokens;
}

function tokenizeFragment(fragment: string, style: TextStyle, target: StyledToken[]) {
  const words = tokenizeRichFragment(fragment);
  for (let w = 0; w < words.length; w++) {
    target.push({ text: words[w], style });
  }
}

function addRunToParagraphs(run: RichRun, paragraphs: StyledToken[][]) {
  const fragments = run.text.split('\n');
  for (let f = 0; f < fragments.length; f++) {
    if (f > 0) {
      paragraphs.push([]);
    }
    tokenizeFragment(fragments[f], run.style, paragraphs[paragraphs.length - 1]);
  }
}

// Tokenizes the rich runs into per-paragraph styled token streams; a '\n' ends a paragraph.
function tokenizeStyledParagraphs(runs: RichRun[]): StyledToken[][] {
  const paragraphs: StyledToken[][] = [[]];
  for (let r = 0; r < runs.length; r++) {
    addRunToParagraphs(runs[r], paragraphs);
  }
  return paragraphs;
}

type RichWrapPacker = { current: StyledToken[]; xAdvance: number };

function startRichToken(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  packer: RichWrapPacker,
  token: StyledToken,
  tokenWidth: number,
  innerWidth: number,
) {
  if (tokenWidth > innerWidth) {
    const remainder = breakLongWordRich(tObj, ctx, token);
    if (remainder.text.length > 0) {
      packer.current.push(remainder);
      packer.xAdvance = styledTokenWidth(ctx, remainder.style, remainder.text);
    }
    return;
  }
  packer.current.push(token);
  packer.xAdvance = tokenWidth;
}

function packRichToken(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  packer: RichWrapPacker,
  token: StyledToken,
  innerWidth: number,
) {
  const tokenWidth = styledTokenWidth(ctx, token.style, token.text);

  if (packer.current.length === 0) {
    startRichToken(tObj, ctx, packer, token, tokenWidth, innerWidth);
    return;
  }

  if (packer.xAdvance + tokenWidth <= innerWidth) {
    packer.current.push(token);
    packer.xAdvance += tokenWidth;
    return;
  }

  pushLineRich(tObj, ctx, packer.current, tObj.height);
  packer.current = [];
  packer.xAdvance = 0;
  startRichToken(tObj, ctx, packer, token, tokenWidth, innerWidth);
}

function wrapStyledParagraph(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  tokens: StyledToken[],
  innerWidth: number,
) {
  const packer: RichWrapPacker = { current: [], xAdvance: 0 };
  for (let w = 0; w < tokens.length; w++) {
    packRichToken(tObj, ctx, packer, tokens[w], innerWidth);
  }
  pushLineRich(tObj, ctx, packer.current, tObj.height);
}

function layoutRichWrap(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  paragraphs: StyledToken[][],
) {
  const innerWidth = tObj.width - tObj.padding[1] - tObj.padding[3];
  for (let p = 0; p < paragraphs.length; p++) {
    wrapStyledParagraph(tObj, ctx, paragraphs[p], innerWidth);
  }
  tObj.height += tObj.padding[2];
  tObj._wrapWidth = tObj.width;
}

function layoutRichNoWrap(
  tObj: TextObject2,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  paragraphs: StyledToken[][],
  setHeight: number,
) {
  let maxTextWidth = 0;
  for (let p = 0; p < paragraphs.length; p++) {
    const line = pushLineRich(tObj, ctx, paragraphs[p], tObj.height);
    if (line.width > maxTextWidth) {
      maxTextWidth = line.width;
    }
  }

  tObj.width = maxTextWidth + tObj.padding[1] + tObj.padding[3];
  tObj.height += tObj.padding[2];

  if (setHeight > tObj.height) {
    tObj.height = setHeight;
  }
}

function setTextRich(tObj: TextObject2, text: string) {
  const ctx = tObj.manager.textRenderCtx;
  const setHeight = tObj.height;
  tObj.text = text;
  tObj.lines = [];
  tObj.height = tObj.padding[0];

  const runs = parseRichText(text, tObj.defaultTextStyle);
  const paragraphs = tokenizeStyledParagraphs(runs);

  if (tObj.textOverflow === 'wrap') {
    layoutRichWrap(tObj, ctx, paragraphs);
  } else {
    layoutRichNoWrap(tObj, ctx, paragraphs, setHeight);
  }
}

export function setText(tObj: TextObject2, text: string, force = false) {
  if (!force && text === tObj.text) return;

  if (tObj.isRichText) {
    setTextRich(tObj, text);
    tObj.isDirty = true;
    return;
  }

  const ctx = tObj.manager.textRenderCtx;
  const setHeight = tObj.height;
  tObj.text = text;
  tObj.lines = [];
  tObj.height = tObj.padding[0];

  if (tObj.textOverflow === 'wrap') {
    const innerWidth = tObj.width - tObj.padding[1] - tObj.padding[3];
    const paragraphs = text.split('\n');

    for (let i = 0; i < paragraphs.length; i++) {
      const words = tokenizeParagraph(paragraphs[i]);
      const currentWords: string[] = [];

      for (let w = 0; w < words.length; w++) {
        const candidate =
          currentWords.length === 0 ? words[w] : joinTokens([...currentWords, words[w]]);
        const candidateWidth = TextLayoutController.getTextWidth(
          ctx,
          tObj.defaultTextStyle.fontId,
          tObj.defaultTextStyle.fontSize,
          candidate,
        );

        if (candidateWidth <= innerWidth) {
          currentWords.push(words[w]);
        } else if (currentWords.length === 0) {
          const remainder = breakLongWord(tObj, ctx, words[w]);
          if (remainder.length > 0) {
            currentWords.push(remainder);
          }
        } else {
          const lineText = joinTokens(currentWords);
          const line = pushLine(tObj, ctx, lineText, tObj.height);

          currentWords.length = 0;
          const wordWidth = TextLayoutController.getTextWidth(
            ctx,
            tObj.defaultTextStyle.fontId,
            tObj.defaultTextStyle.fontSize,
            words[w],
          );
          if (wordWidth > innerWidth) {
            const remainder = breakLongWord(tObj, ctx, words[w]);
            if (remainder.length > 0) {
              currentWords.push(remainder);
            }
          } else {
            currentWords.push(words[w]);
          }
        }
      }

      if (currentWords.length > 0) {
        pushLine(tObj, ctx, joinTokens(currentWords), tObj.height);
      }
    }

    tObj.height += tObj.padding[2];
    tObj._wrapWidth = tObj.width;
  } else {
    const textLines = text.split('\n');
    let maxTextWidth = 0;

    for (let i = 0; i < textLines.length; i++) {
      const yPosition = tObj.height;
      const width = TextLayoutController.getTextWidth(
        ctx,
        tObj.defaultTextStyle.fontId,
        tObj.defaultTextStyle.fontSize,
        textLines[i],
      );
      const height = tObj.defaultTextStyle.fontSize * tObj.defaultTextStyle.lineHeight;

      tObj.height += height;
      if (width > maxTextWidth) {
        maxTextWidth = width;
      }

      tObj.lines.push({
        text: textLines[i],
        visibleText: textLines[i],
        width,
        height,
        yPosition,
      });
    }

    tObj.width = maxTextWidth + tObj.padding[1] + tObj.padding[3];
    tObj.height += tObj.padding[2];

    if (setHeight > tObj.height) {
      tObj.height = setHeight;
    }
  }

  tObj.isDirty = true;
}

function computeCaretOffset(tObj: TextObject2): number {
  if (tObj.lines.length === 0) {
    return tObj.text.length;
  }

  const innerClickX = tObj.relativeMousePos.x + tObj.width / 2 - tObj.padding[3];
  const innerClickY = tObj.relativeMousePos.y + tObj.height / 2 - tObj.padding[0];

  let lineIndex = 0;
  for (let i = 0; i < tObj.lines.length; i++) {
    if (tObj.lines[i].yPosition - tObj.padding[0] <= innerClickY) {
      lineIndex = i;
    } else {
      break;
    }
  }

  const ctx = tObj.manager.textRenderCtx;
  const { fontId, fontSize } = tObj.defaultTextStyle;
  const lineText = tObj.lines[lineIndex].text;

  let lo = 0; // the lowest index that still fits within innerClickX (i.e., the text up to lo chars wide ≤ click X)
  let hi = lineText.length; // the highest index that might still fit

  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (
      TextLayoutController.getTextWidth(ctx, fontId, fontSize, lineText.slice(0, mid)) <=
      innerClickX
    ) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }

  if (lo < lineText.length) {
    const wLo = TextLayoutController.getTextWidth(ctx, fontId, fontSize, lineText.slice(0, lo));
    const wNext = TextLayoutController.getTextWidth(
      ctx,
      fontId,
      fontSize,
      lineText.slice(0, lo + 1),
    );
    if (Math.abs(innerClickX - wNext) < Math.abs(innerClickX - wLo)) {
      lo++;
    }
  }

  let pos = 0;
  for (let i = 0; i < lineIndex; i++) {
    const found = tObj.text.indexOf(tObj.lines[i].text, pos);
    if (found !== -1) {
      pos = found + tObj.lines[i].text.length;
    }
  }
  const lineStart = tObj.text.indexOf(tObj.lines[lineIndex].text, pos);
  const resolvedLineStart = lineStart !== -1 ? lineStart : pos;
  return resolvedLineStart + lo;
}

function stopTextEdit(tObj: TextObject2) {
  if (tObj.editTextArea === null) return;
  setText(tObj, tObj.editTextArea.value);
  tObj._isEditing = false;
  tObj._dragSelectionAnchor = null;
  tObj.editTextArea.style.pointerEvents = 'none';
  tObj._editAreaDirty = true;
  tObj.manager.cursor.allowTyping = true;
}

function startTextEdit(tObj: TextObject2) {
  if (tObj.editTextArea !== null) return;

  const el = document.createElement('textarea');
  el.classList.add('text-editor');

  const fontSize = tObj.defaultTextStyle.fontSize;
  const lineHeightPx = fontSize * tObj.defaultTextStyle.lineHeight;
  el.style.fontSize = `${fontSize}px`;
  el.style.lineHeight = `${lineHeightPx}px`;
  el.style.fontFamily = tObj.defaultTextStyle.fontId;
  el.style.color = colorToHex(tObj.defaultTextStyle.color);
  el.style.textAlign = 'left';
  el.style.resize = 'none';
  el.style.background = 'transparent';
  el.style.border = 'none';
  el.style.outline = 'none';
  el.style.padding = '0';
  el.style.margin = '0';
  el.style.overflow = 'hidden';
  el.style.position = 'absolute';
  el.style.transformOrigin = 'top left';
  el.style.boxSizing = 'content-box';

  el.readOnly = tObj.editMode === TextObject2EditMode.OPEN_TO_SELECT;
  el.value = tObj.text;
  if (tObj.maxLength > 0) {
    el.maxLength = tObj.maxLength;
  }

  const caretOffset = computeCaretOffset(tObj);

  tObj._isEditing = true;
  tObj.manager.cursor.allowTyping = false;

  el.addEventListener('blur', () => stopTextEdit(tObj));

  tObj.manager.DOM.overlayContainer.appendChild(el);
  tObj.editTextArea = el;

  tObj._pendingFocus = true;
  tObj._pendingCaretOffset = caretOffset;
}

function update(tObj: TextObject2): boolean {
  let isDirty = false;

  if (tObj._pendingFocus && tObj.editTextArea !== null) {
    tObj._pendingFocus = false;
    tObj.editTextArea.focus({ preventScroll: true });
    tObj.editTextArea.setSelectionRange(tObj._pendingCaretOffset, tObj._pendingCaretOffset);
    if (
      tObj.editMode === TextObject2EditMode.OPEN_TO_EDIT ||
      tObj.editMode === TextObject2EditMode.OPEN_TO_SELECT
    ) {
      tObj._dragSelectionAnchor = tObj._pendingCaretOffset;
    }
  }

  if (tObj._isEditing && tObj._dragSelectionAnchor !== null && tObj.editTextArea !== null) {
    if (isMouseDown()) {
      const anchor = tObj._dragSelectionAnchor;
      const [absPos, absScale] = getAbsolutePosAndScale(tObj);
      tObj.relativeMousePos.x = (tObj.manager.mousePos.x - absPos.x) / absScale;
      tObj.relativeMousePos.y = (tObj.manager.mousePos.y - absPos.y) / absScale;
      const current = computeCaretOffset(tObj);
      const selStart = Math.min(anchor, current);
      const selEnd = Math.max(anchor, current);
      const direction = anchor <= current ? 'forward' : 'backward';
      tObj.editTextArea.setSelectionRange(selStart, selEnd, direction);
    } else {
      tObj._dragSelectionAnchor = null;
    }
  }

  if (
    tObj.editMode === TextObject2EditMode.OPEN_TO_EDIT ||
    tObj.editMode === TextObject2EditMode.OPEN_TO_SELECT
  ) {
    if (mouseDownThisFrame() && tObj.mouseDownOnMe) {
      startTextEdit(tObj);
      isDirty = true;
    }
  } else if (tObj.editMode === TextObject2EditMode.CLICK_TO_EDIT) {
    if (tObj.clickedThisFrame) {
      startTextEdit(tObj);
      isDirty = true;
    }
  }

  if (
    tObj.editMode === TextObject2EditMode.OPEN_TO_EDIT ||
    tObj.editMode === TextObject2EditMode.OPEN_TO_SELECT
  ) {
    if (tObj.highlighted && !tObj._hasTextCursor) {
      tObj._hasTextCursor = true;
      setMouseCursor('text');
    } else if (!tObj.highlighted && tObj._hasTextCursor) {
      tObj._hasTextCursor = false;
      resetMouseCursor();
    }
  }

  if (tObj._pendingRemoval) {
    tObj._pendingRemoval = false;
    if (tObj.editTextArea !== null) {
      tObj.editTextArea.remove();
      tObj.editTextArea = null;
    }
  }

  if (tObj._editAreaDirty) {
    tObj._editAreaDirty = false;
    tObj._pendingRemoval = true;
    isDirty = true;
  }

  if (tObj.textOverflow === 'wrap' && tObj.width !== tObj._wrapWidth) {
    setText(tObj, tObj.text, true);
    isDirty = true;
  }

  const handle = tObj.handle;
  if (handle !== null) {
    if (tObj.canEditSize !== handle.allowDrag) {
      handle.allowDrag = tObj.canEditSize;
      handle.allowHighlight = tObj.canEditSize;
      if (tObj.canEditSize) {
        Container.setContainerStyle(handle, { ...SHAPE_STYLES.NONE, fillColor: COLORS.WHITE_100 });
      } else {
        Container.setContainerStyle(handle, null);
      }
    }

    if (tObj.canEditSize) {
      if (handle.dragged) {
        const newWidth = Math.max(TEXTOBJ2_MIN_WIDTH, handle.anchoredPos.offset.x * 2);
        const newHeight = Math.max(TEXTOBJ2_MIN_HEIGHT, handle.anchoredPos.offset.y * 2);
        tObj.width = newWidth;
        tObj.height = newHeight;
        isDirty = true;
      } else {
        handle.anchoredPos.offset.x = tObj.width / 2;
        handle.anchoredPos.offset.y = tObj.height / 2;
      }
    }
  }

  return isDirty;
}

export function createPackage(
  textObject: TextObject2,
  position: V2,
  scale: number,
  rotation: number = 0,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (textObject.canEditSize) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      rotation,
      positionX: position.x,
      positionY: position.y,
      width: textObject.width,
      height: textObject.height,
      opacity: textObject.opacity,

      shapeStyle: { ...SHAPE_STYLES.NONE, strokeWeight: 1, strokeColor: COLORS.WHITE_100 },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };

    rPackage.push(rect);
  }

  if (textObject._isEditing) {
    return rPackage;
  }

  for (let i = 0; i < textObject.lines.length; i++) {
    const line = textObject.lines[i];
    if (textObject.isRichText && line.segments && line.segments.length > 0) {
      pushRichLinePackage(textObject, line, position, scale, rotation, rPackage);
    } else {
      pushPlainLinePackage(textObject, line, position, scale, rotation, rPackage);
    }
  }

  return rPackage;
}

// Rotates a text item's world anchor about the text object's center and tilts the
// glyphs, so a rotated parent spins the whole text block coherently about `position`.
function applyTextRotation(rText: RenderPackageText, position: V2, rotation: number) {
  if (rotation === 0) {
    return;
  }
  const anchor = rotateV2({ x: rText.positionX - position.x, y: rText.positionY - position.y }, rotation);
  rText.positionX = position.x + anchor.x;
  rText.positionY = position.y + anchor.y;
  rText.rotation = rotation;
}

function pushPlainLinePackage(
  textObject: TextObject2,
  line: Line,
  position: V2,
  scale: number,
  rotation: number,
  rPackage: RenderPackageItem[],
) {
  const positionY =
    position.y -
    scale * textObject.height * 0.5 +
    scale * textObject.defaultTextStyle.fontSize * CANVAS_TEXT_CAP_MARGIN_RATIO +
    scale * line.yPosition;

  const rText: RenderPackageText = {
    type: RenderItemType.text,
    ...EMPTY_RENDER_TRANSFORM,
    scale,
    positionX: position.x - scale * textObject.width * 0.5 + scale * textObject.padding[3],
    positionY,
    hAlign: TextAlignHor.LEFT,
    fillColor: textObject.defaultTextStyle.color,
    strokeWeight: 0,
    strokeColor: [0, 0, 0],
    size: textObject.defaultTextStyle.fontSize,
    text: line.visibleText,
    fontId: textObject.defaultTextStyle.fontId,
    shadow: textObject.defaultTextStyle.shadow || null,
    opacity: 1,
  };
  applyTextRotation(rText, position, rotation);
  rPackage.push(rText);
}

// Largest font size on the line, so all segments share one cap-anchored baseline.
function lineMaxFontSize(line: Line, fallback: number): number {
  let maxSize = fallback;
  const segments = line.segments || [];
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].style.fontSize > maxSize) {
      maxSize = segments[i].style.fontSize;
    }
  }
  return maxSize;
}

// Largest cap-ascent on the line, used as the common baseline anchor for mixed font sizes.
function lineMaxAscent(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  line: Line,
  defaultStyle: TextStyle,
): number {
  let maxAscent = TextLayoutController.getCapAscent(ctx, defaultStyle.fontId, defaultStyle.fontSize);
  const segments = line.segments || [];
  for (let i = 0; i < segments.length; i++) {
    const ascent = TextLayoutController.getCapAscent(
      ctx,
      segments[i].style.fontId,
      segments[i].style.fontSize,
    );
    if (ascent > maxAscent) {
      maxAscent = ascent;
    }
  }
  return maxAscent;
}

function pushRichLinePackage(
  textObject: TextObject2,
  line: Line,
  position: V2,
  scale: number,
  rotation: number,
  rPackage: RenderPackageItem[],
) {
  const ctx = textObject.manager.textRenderCtx;
  const segments = line.segments || [];
  const maxFontSize = lineMaxFontSize(line, textObject.defaultTextStyle.fontSize);
  const maxAscent = lineMaxAscent(ctx, line, textObject.defaultTextStyle);
  const capTopY =
    position.y -
    scale * textObject.height * 0.5 +
    scale * maxFontSize * CANVAS_TEXT_CAP_MARGIN_RATIO +
    scale * line.yPosition;
  const leftX = position.x - scale * textObject.width * 0.5 + scale * textObject.padding[3];

  let advanceX = 0;
  for (let s = 0; s < segments.length; s++) {
    const segment = segments[s];
    const ascent = TextLayoutController.getCapAscent(
      ctx,
      segment.style.fontId,
      segment.style.fontSize,
    );
    const rText: RenderPackageText = {
      type: RenderItemType.text,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: leftX + scale * advanceX,
      positionY: capTopY + scale * (maxAscent - ascent),
      hAlign: TextAlignHor.LEFT,
      fillColor: segment.style.color,
      strokeWeight: 0,
      strokeColor: [0, 0, 0],
      size: segment.style.fontSize,
      text: segment.visibleText,
      fontId: segment.style.fontId,
      shadow: segment.style.shadow || null,
      opacity: 1,
    };
    applyTextRotation(rText, position, rotation);
    rPackage.push(rText);
    advanceX += segment.width;
  }
}

// Returns the caret position right after the last character of the last line,
// as a V2 relative to the textObject's space (origin at its center).
export function getEndCaretPosition(tObj: TextObject2): V2 {
  const leftX = -tObj.width / 2 + tObj.padding[3];

  if (tObj.lines.length === 0) {
    return v2(leftX, -tObj.height / 2 + tObj.padding[0]);
  }

  const lastLine = tObj.lines[tObj.lines.length - 1];
  return v2(leftX + lastLine.width, lastLine.yPosition + lastLine.height);
}

// Returns the character length of the first line, or 0 if there is no text.
export function getFirstLineLength(tObj: TextObject2): number {
  if (tObj.lines.length === 0) {
    return 0;
  }
  return tObj.lines[0].width;
}
